import {
  CredentialStore, createMemoryCredentialStorage,
  type CredentialStorage, type KeyResolver, type Principal, type StoredCredential,
} from './credentialStore';
import { keyFromEnvironmentSecret } from './envKey';
import { keyFromUserPassphrase } from '../auth/session';
import { createGraphCredentialStorage } from './graphCredentialStorage';

/**
 * F 332 — A DURABLE CREDENTIAL STORAGE ADAPTER.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * F 327 built `CredentialStore` and its pluggable `CredentialStorage` seam, and
 * every test used `createMemoryCredentialStorage`. Nothing in the app ever
 * CONSTRUCTED a store: `grep -rn "new CredentialStore" src/` found no runtime
 * construction at all. So the credential rows were pointers to a store that did
 * not exist in the product.
 *
 * The memory adapter is not a substitute here. A key saved into a memory-backed
 * store would be gone on reload, which is not a credential store, it is a
 * clipboard. So this adapter persists the ENCRYPTED record — the thing the store
 * produced — and never the plaintext, because the plaintext never leaves
 * `CredentialStore.set()` in the first place.
 *
 * THE STORE DOES NOT ASSUME A BROWSER; THE ADAPTER DOES
 * -----------------------------------------------------
 * `credentialStore.ts` is environment-agnostic on purpose. IndexedDB is a
 * browser concern, so it lives here rather than being pushed into the core.
 */

/**
 * IndexedDB-backed credential storage.
 *
 * Falls back to the in-memory adapter when IndexedDB is unavailable — a server,
 * a test, a private window that blocks it. The fallback is loud in the shape it
 * returns, not silent in behaviour: `durable` tells the caller whether what was
 * written will still be there tomorrow.
 */
export interface DurableCredentialStorage {
  storage: CredentialStorage;
  durable: boolean;
}

const DB_NAME = 'freshcards:credentials';
const STORE = 'records';
const VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
        t.oncomplete = () => db.close();
      }),
  );
}

/**
 * Build a durable store.
 *
 * `seed` is applied on an EMPTY database only, which is what makes a re-mount
 * idempotent: opening the settings page twice does not re-seed over a key the
 * user has since rotated.
 */
export async function createIndexedDbCredentialStorage(
  seed: StoredCredential[] = [],
): Promise<DurableCredentialStorage> {
  try {
    const existing = await tx<StoredCredential[]>('readonly', (s) => s.getAll());
    if (existing.length === 0 && seed.length > 0) {
      await tx('readwrite', (s) => {
        for (const r of seed) s.put(r);
        // A write request per record; resolve when the last one lands.
        return s.getAll() as unknown as IDBRequest<StoredCredential[]>;
      });
    }
    return {
      durable: true,
      storage: {
        async get(id) { return (await tx<StoredCredential | undefined>('readonly', (s) => s.get(id))) ?? undefined; },
        async put(rec) { await tx('readwrite', (s) => s.put(rec) as unknown as IDBRequest<IDBValidKey>); },
        async delete(id) { await tx('readwrite', (s) => s.delete(id) as unknown as IDBRequest<undefined>); },
        async list() { return await tx<StoredCredential[]>('readonly', (s) => s.getAll()); },
      },
    };
  } catch {
    // No IndexedDB: degrade to memory and say so, rather than pretending.
    return { durable: false, storage: createMemoryCredentialStorage() };
  }
}

/**
 * The store the settings page uses.
 *
 * `resolveKey` comes from F 327's `envKey` — the key is DERIVED from an
 * environment secret, never the secret itself, so this adapter never sees a
 * passphrase and never stores one.
 */
export function createAppCredentialStore(opts: {
  storage: CredentialStorage;
  resolveKey: KeyResolver;
  principal?: Principal;
  /** F 425 — the salt behind a PRE-DERIVED key. See `CredentialStoreOptions`. */
  saltFor?: (principal: Principal) => string | Promise<string>;
}): CredentialStore {
  return new CredentialStore({
    storage: opts.storage,
    resolveKey: opts.resolveKey,
    defaultPrincipal: opts.principal ?? { scope: 'operator', id: 'operator-1' },
    saltFor: opts.saltFor,
  });
}

/**
 * F 370 — THE STORE, CONSTRUCTED IN ONE PLACE.
 *
 * WHY THIS EXISTS. `Settings.tsx:350` built the product's only runtime
 * `CredentialStore`, and `AIChatPanel` read its key from two sources that are
 * both absent in a production build (`window.__FC_AGENT_KEY__` — which nothing
 * in the repo ever sets — and `import.meta.env.VITE_GROQ_KEY` — which no build
 * script sets). So the chat sent `Authorization: Bearer ` with an EMPTY value
 * and Groq answered 401 "Invalid API Key", which the chat then printed as
 * "Operator unavailable: Invalid API Key" — a message that is true about the
 * provider and useless about the cause.
 *
 * Two surfaces need this store. When the second one rebuilt it inline, it
 * re-created the original defect one layer up: a fix for "the user" that left
 * the sibling broken. So the construction is here, once, and both callers use
 * it.
 *
 * FAIL-CLOSED, AND IT SAYS WHY. `VITE_FC_CREDENTIAL_SECRET` is a BUILD-TIME
 * variable. When it is absent there is no way to derive the sealing key, and a
 * store that cannot seal is not a store — so this returns a reason rather than
 * a half-built object, and callers surface the reason instead of pretending a
 * key is missing.
 */
export interface AppCredentialStoreResult {
  store: CredentialStore | null;
  /** Human-readable, safe to show. Null when `store` is non-null. */
  reason: string | null;
}

/**
 * F 374 — the inputs a caller may bring to the store.
 *
 * `passphrase` is the user's password, held in memory for the life of the
 * session. When it is present it is the KEY SOURCE, and the environment secret
 * is not consulted at all.
 */
export interface AppCredentialStoreOptions {
  /**
   * The user's passphrase. NEVER persisted, NEVER sent anywhere.
   *
   * When present, the AES-GCM key is derived from it with
   * `keyFromPassphrase` and the store is scoped to `principal`.
   */
  passphrase?: string;
  /**
   * F 397 — A KEY RESTORED FROM THE SESSION RECORD, used only when there is
   * no passphrase (i.e. after a reload). It is the KDF-derived key, never a
   * passphrase, and it is consulted only after the passphrase resolver has
   * declined. `undefined` keeps the pre-F397 behaviour exactly.
   */
  sessionKey?: string;
  /**
   * F 425 — THE SALT BEHIND `sessionKey`.
   *
   * The restored key was derived from a passphrase with a KNOWN salt. Anything
   * sealed under it must record that salt, or the node will carry a random salt
   * that no passphrase reproduces and the Lane Bridge cannot open it. Omit it
   * and the store falls back to a random per-record salt — correct only for a
   * resolver that derives, never for one that returns a pre-derived key.
   */
  sessionKeySalt?: string;
  /**
   * The principal the store reads and writes under.
   *
   * Defaults to the historic `operator-1`. A session supplies its own, so
   * credentials are scoped to a PERSON rather than to the machine.
   */
  principal?: Principal;
}

/**
 * F 374 — THE STORE, CONSTRUCTED IN ONE PLACE.
 *
 * F 370 built this and it is still the single constructor both surfaces use.
 * F 374 adds ONE thing to it: a passphrase path.
 *
 * WHY THE PASSPHRASE PATH IS NOT OPTIONAL-EXTRA
 * ---------------------------------------------
 * The environment secret is a `VITE_*` variable, and Vite inlines those into
 * the public bundle. Measured on production on 2026-10-20, the value of
 * `VITE_FC_CREDENTIAL_SECRET` is readable in `index-1B6bQvxb.js`. So the key
 * that sealed every stored credential was downloadable by anyone — real
 * encryption, with a public key.
 *
 * A passphrase that never leaves the browser is not public. So when a session
 * supplies one, the passphrase IS the key source and the environment secret is
 * not consulted. `envKey.ts` already encodes the policy: `resolverFor` gives
 * the passphrase resolver priority for a `user` scope, because "a user secret
 * must be passphrase-gated, or any code in the process reads it".
 *
 * THE ENV PATH IS NOT REMOVED. A credential sealed under the env-derived key
 * would become unreadable if the resolver silently changed, and a store that
 * cannot open what it wrote is worse than a store that says which key it
 * expects. So the two paths are kept, and the caller chooses.
 */
export async function buildAppCredentialStore(
  opts: AppCredentialStoreOptions = {},
): Promise<AppCredentialStoreResult> {
  const env =
    typeof import.meta !== 'undefined'
      ? (import.meta as unknown as { env?: Record<string, string> }).env
      : undefined;
  const secret = env?.VITE_FC_CREDENTIAL_SECRET;
  const passphrase = opts.passphrase;
  // F 397 — THE RESTORED KEY. After a reload there is no passphrase, only
  // the key that was derived from it at sign-in. It is used ONLY as the last
  // resort, after the passphrase resolver, because a live passphrase is
  // strictly better: it re-derives from the principal rather than trusting a
  // stored blob.
  const sessionKey = opts.sessionKey;
  const sessionKeySalt = opts.sessionKeySalt;
  const principal = opts.principal;

  if (!passphrase && !secret && !sessionKey) {
    return {
      store: null,
      reason:
        'Credential storage is not configured in this build (VITE_FC_CREDENTIAL_SECRET is absent), so no API key can be stored or read.',
    };
  }
  try {
    const durable = await createIndexedDbCredentialStorage();
    // F 474 — THE GRAPH IS A READ SOURCE FOR THE STORE.
    //
    // The store's storage seam is the one place that says where a sealed
    // credential record lives, and until F 474 that was always the browser's
    // IndexedDB. That is why a key saved on the laptop did not exist on the
    // phone: it was not lost, it was in a place the phone does not have.
    //
    // So the storage is now LOCAL + GRAPH: a read that misses locally falls
    // through to the graph, whose node is converted back into the store's own
    // record. Every consumer of the store — the chat, the settings page, the
    // provider slot — sees the key with no other change, because the store is
    // what they all read through.
    //
    // The graph read costs no network call: `RemoteState.init()` has already
    // hydrated the whole graph into the in-memory cache that
    // `remoteAdapter.load()` reads.
    const storage = createGraphCredentialStorage({
      local: durable.storage,
      principal: opts.principal,
    });
    return {
      store: createAppCredentialStore({
        storage,
        resolveKey: passphrase
          ? keyFromUserPassphrase({ passphrase })
          : sessionKey
            // The stored key is already derived and already bound to this
            // principal — deriving it again is not possible (PBKDF2 is
            // one-way) and not necessary. It is returned verbatim.
            //
            // F 425: because this resolver IGNORES the record's salt, the store
            // must not invent one. `saltFor` pins the node to the salt behind
            // this key, which is what lets a passphrase holder reproduce it.
            ? async () => sessionKey
            : keyFromEnvironmentSecret({
                variable: 'VITE_FC_CREDENTIAL_SECRET',
                read: () => secret as string,
              }),
        principal,
        saltFor: passphrase || !sessionKey ? undefined : () => sessionKeySalt as string,
      }),
      reason: null,
    };
  } catch (e) {
    return {
      store: null,
      reason: `Credential storage could not be opened: ${e instanceof Error ? e.message : String(e)}`,
    };
  }
}

/**
 * F 370 — resolve one provider's key by NAME.
 *
 * `CredentialStore.resolve()` takes an input OBJECT and returns
 * `Promise<string | null>` — it does not return a record with a `.value`. The
 * credential PipelineFaces writes is `{ provider, name: 'pipeline',
 * kind: 'api_key' }` (`PipelineFaces.tsx:64`), so 'pipeline' is the name the
 * chat must ask for. Getting this wrong returns null, which presents as "you
 * have no key" for a key that is stored and readable.
 */
export async function resolveProviderKey(
  store: CredentialStore,
  provider: string,
  name = 'pipeline',
): Promise<string | null> {
  try {
    return await store.resolve({ provider: provider as never, name });
  } catch {
    // A resolve failure is not a key. Report absence rather than propagating.
    return null;
  }
}